import {useContext, useState} from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import {
  ActivityIndicator,
  Button,
  Subheading,
  Surface,
  Text,
} from 'react-native-paper';
import {DrawerScreenProps} from '@react-navigation/drawer';

import {Person, PersonInfo, usePeople} from '../hooks/usePersonInfo';
import {
  CreditTransaction,
  filterCreditTransactions,
  filterReservations,
  filterSubscribers,
  LimitedPartner,
  Reservation,
  Subscriber,
  useCreditTransactions,
  useCreditTypes,
  useFetchLimitedPartners,
  useFetchMembers,
  useReservations,
  Member,
  useSubscribers,
  useReservationYears,
} from '../hooks/useDomainData';
import {PersonInfoContext} from './Stack';
import {SubscriberComponent} from './Subscribers';
import {CreditBalances} from '../components/CreditBalances';
import {ReservationEditor} from '../components/FormikStoreEditors/ReservationEditor';
import {CreditTransactionEditor} from '../components/FormikStoreEditors/CreditTransactionEditor';
import {CreditTransactionList} from './Transactions';
import {ReservationList} from './Reservations';

// undefined means no results yet, empty array means no results but query is done
function getInvestorDetails(
  personInfo?: PersonInfo,
  personId?: number,
  memberId?: number,
  limitedPartnerId?: number,
  subscriberId?: number,
  people?: readonly Person[],
  members?: readonly Member[],
  limitedPartners?: readonly LimitedPartner[],
  subscribers?: readonly Subscriber[],
): Subscriber[] | undefined {
  let peopleIds = people?.map(person => person.personId ?? -1);

  let filteredSubscribers: Subscriber[] | undefined = [];

  if (personInfo?.isAdmin || personInfo?.isGuestRelations) {
    // console.log('Doing an admin query for investor details');
    if (!personId && !memberId && !limitedPartnerId && !subscriberId) {
      // console.log('no query parameters provided. no query possible');
      return undefined;
    }

    if (personId) {
      peopleIds = [personId];
      // console.log('people query: ' + peopleIds.join(', '));
    }
    if (memberId && members) {
      members = Array.from(members).filter(
        member => member.memberId + '' === memberId + '',
      );
      // console.log(
      //   'member query: ' + members?.map(member => member.memberId).join(', '),
      // );
    }
    if (limitedPartnerId && limitedPartners) {
      limitedPartners = Array.from(limitedPartners).filter(
        lp => lp.limitedPartnerId + '' === limitedPartnerId + '',
      );
      // console.log(
      //   'lp query: ' +
      //     limitedPartners?.map(lp => lp.limitedPartnerId).join(', '),
      // );
    }
    if (subscriberId && subscribers) {
      subscribers = Array.from(subscribers).filter(
        sub => sub.subscriberId + '' === subscriberId + '',
      );
      // console.log('sub query: ' + subscribers?.map(sub => sub.subscriberId));
    }
  }

  if (!personInfo?.isAdmin && !personInfo?.isGuestRelations) {
    peopleIds = [personInfo?.person.personId ?? -1];
  }
  console.log('before filterSubscribers');
  filteredSubscribers = filterSubscribers(
    subscribers,
    limitedPartners,
    members,
    peopleIds,
  );
  console.log('after filterSubscribers');

  // console.log('tree is: ' + JSON.stringify(filteredSubscribers, null, 0));
  return filteredSubscribers;
}

type DrawerParamList = {
  Search:
    | {
        type:
          | 'person'
          | 'member'
          | 'limited-partner'
          | 'subscriber'
          | undefined;
        id: number;
      }
    | undefined;
};

type Props = DrawerScreenProps<DrawerParamList, 'Search'>;

export const InvestorDetails = ({route}: Props) => {
  const personInfo = useContext(PersonInfoContext);
  let subscribers = useSubscribers();
  const creditTransactions = useCreditTransactions();
  const creditTypes = useCreditTypes();
  const reservations = useReservations();
  const reservationYears = useReservationYears();
  const people = usePeople();
  const members = useFetchMembers();
  let limitedPartners = useFetchLimitedPartners();

  const [resDialogVisible, setResDialogVisible] = useState(false);
  const [txnDialogVisible, setTxnDialogVisible] = useState(false);
  const [currentCreditTransaction, setCurrentCreditTransaction] = useState<
    CreditTransaction | undefined
  >();
  const [currentReservation, setCurrentReservation] = useState<
    Reservation | undefined
  >();

  const type = route.params?.type;
  const id = route.params?.id;

  // console.log('we got params! ' + JSON.stringify(route.params));
  // console.log('we are a type? ' + route.params?.type);
  // console.log('we are an id? ' + route.params?.id);

  console.log('before getInvestorDetails');
  subscribers = getInvestorDetails(
    personInfo?.filteredPersonInfo?.authPerson,
    type === 'person' ? id : undefined,
    type === 'member' ? id : undefined,
    type === 'limited-partner' ? id : undefined,
    type === 'subscriber' ? id : undefined,
    people,
    members,
    limitedPartners,
    subscribers,
  );
  console.log('after getInvestorDetails');

  const allLimitedPartners: Set<LimitedPartner> = new Set();
  subscribers?.forEach(sub => {
    sub.limitedPartners?.map(lp => {
      allLimitedPartners.add(lp);
    });
  });

  // console.log('all associated LPs: ' + allLimitedPartners.size);
  const filteredTxns = filterCreditTransactions(
    creditTransactions ? Array.from(creditTransactions) : [],
    creditTypes ? Array.from(creditTypes) : [],
    Array.from(allLimitedPartners),
    members ? Array.from(members) : [],
    people ? Array.from(people) : [],
    reservationYears ? Array.from(reservationYears) : undefined,
  );
  console.log('after filterCreditTransactions');
  const filteredReservations = filterReservations(
    reservations ? Array.from(reservations) : undefined,
    Array.from(allLimitedPartners),
    members ? Array.from(members) : undefined,
    people ? Array.from(people) : undefined,
    reservationYears ? Array.from(reservationYears) : undefined,
  );
  console.log('after filterReservations');

  if (
    (personInfo?.filteredPersonInfo?.authPerson?.isAdmin ||
      personInfo?.filteredPersonInfo?.authPerson?.isGuestRelations) &&
    id === undefined
  ) {
    return (
      <View style={[styles.content, styles.container, styles.center]}>
        <Subheading style={styles.center}>
          Please select an investor from search
        </Subheading>
      </View>
    );
  }

  const handleCreateEditReservation = (reservation?: Reservation) => {
    // console.log('should edit reservation: ' + reservation?.ref.path);
    setCurrentReservation(reservation);
    setResDialogVisible(true);
  };

  const handleCreateEditCreditTransaction = (
    creditTransaction?: CreditTransaction,
  ) => {
    // console.log('should edit credit transactionPath: ' + creditTransaction);
    setCurrentCreditTransaction(creditTransaction);
    setTxnDialogVisible(true);
  };

  return (
    <>
      <ScrollView style={[styles.container, styles.content]}>
        {!subscribers && (
          <Surface style={styles.focusedSurface}>
            <View style={styles.center}>
              <Text style={styles.center}>Loading investor details...</Text>
              <ActivityIndicator size={'small'} />
            </View>
          </Surface>
        )}
        {subscribers?.map(sub => (
          <Surface style={styles.focusedSurface} key={sub.subscriberId}>
            <SubscriberComponent subscriber={sub} alwaysOpen={true} />
          </Surface>
        ))}

        <View style={styles.divider} />

        <CreditBalances txns={filteredTxns} />

        <Surface style={[styles.focusedSurface, styles.padding20]}>
          <View style={styles.horizontalCentered}>
            <Subheading>Reservations</Subheading>
            {(personInfo?.filteredPersonInfo?.authPerson?.isAdmin ||
              personInfo?.filteredPersonInfo?.authPerson?.isGuestRelations) && (
              <Button
                mode="outlined"
                onPress={() => handleCreateEditReservation()}>
                Create Reservation
              </Button>
            )}
          </View>
          <ReservationList
            loading={false}
            reservations={filteredReservations}
            transactions={filteredTxns}
            limitedPartners={Array.from(allLimitedPartners)}
            authPersonInfo={personInfo?.filteredPersonInfo?.authPerson}
          />
        </Surface>

        <Surface style={[styles.focusedSurface, styles.padding20]}>
          <View style={styles.horizontalCentered}>
            <Subheading>Credit Transactions</Subheading>
            {(personInfo?.filteredPersonInfo?.authPerson?.isAdmin ||
              personInfo?.filteredPersonInfo?.authPerson?.isGuestRelations) && (
              <Button
                mode="outlined"
                onPress={() => handleCreateEditCreditTransaction()}>
                Create Transaction
              </Button>
            )}
          </View>
          <CreditTransactionList
            loading={false}
            reservations={filteredReservations}
            transactions={filteredTxns}
            limitedPartners={Array.from(allLimitedPartners)}
            members={members}
            people={people}
            authPersonInfo={personInfo?.filteredPersonInfo?.authPerson}
          />
        </Surface>
      </ScrollView>
      <ReservationEditor
        allLimitedPartners={Array.from(allLimitedPartners)}
        dialogVisible={resDialogVisible}
        setDialogVisible={(visible: boolean) => {
          setResDialogVisible(visible);
          if (!visible) {
            setCurrentReservation(undefined);
          }
        }}
        transactions={filteredTxns}
        reservation={currentReservation}
      />

      <CreditTransactionEditor
        allLimitedPartners={Array.from(allLimitedPartners)}
        filteredReservations={filteredReservations ?? []}
        dialogVisible={txnDialogVisible}
        setDialogVisible={(visible: boolean) => {
          setTxnDialogVisible(visible);
          if (!visible) {
            setCurrentCreditTransaction(undefined);
          }
        }}
        creditTransaction={currentCreditTransaction}
      />
    </>
  );
};

const styles = StyleSheet.create({
  divider: {
    paddingTop: 10,
  },
  container: {
    flex: 1,
    position: 'relative',
  },
  content: {
    padding: 10,
  },
  center: {
    width: '100%',
    alignSelf: 'center',
    textAlign: 'center',
    justifyContent: 'center',
  },
  focusedSurface: {
    padding: 10,
    margin: 10,
    elevation: 5,
  },
  padding20: {
    padding: 20,
  },
  horizontalCentered: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignContent: 'center',
  },
});
